import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { fundCostService } from '@/services/fundCostService';
import { APIError } from '@/utils/api-client';
import { bankIncomingQueryKeys } from './useBankIncoming';
import type {
  FundCost,
  FundCostCategory,
  FundCostCreateResponse,
  FundCostPage,
  FundCostCreate,
  FundCostUpdate,
  FundCostRecognitionEntry,
  FundCostsSummary,
} from '@/types/fund-costs';
import { queryKeys } from './queryKeys';

export const useFundCosts = (
  fundId: string,
  params?: {
    cost_category?: FundCostCategory;
    page?: number;
    page_size?: number;
  }
) => {
  return useApiQuery<FundCostPage>(
    queryKeys.fundCosts.list(fundId, params),
    () => fundCostService.list(fundId, params),
    {
      enabled: !!fundId,
    }
  );
};

export const useFundCostsSummary = (fundId: string) => {
  return useApiQuery<FundCostsSummary>(
    queryKeys.fundCosts.summary(fundId),
    () => fundCostService.summary(fundId),
    {
      enabled: !!fundId,
    }
  );
};

export const useFundCostRecognition = (fundId: string, costId: string) =>
  useApiQuery<FundCostRecognitionEntry[]>(
    queryKeys.fundCosts.recognition(fundId, costId),
    () => fundCostService.recognition(fundId, costId),
    { enabled: !!fundId && !!costId }
  );

export const useCreateFundCost = (fundId: string) => {
  return useApiMutation<FundCostCreateResponse, APIError, FundCostCreate>(
    (data) => fundCostService.create(fundId, data),
    {
      invalidateKeys: [
        queryKeys.fundCosts.all,
        queryKeys.fundTransactions.lists(fundId),
      ],
      successMessage: 'Cost added successfully',
    }
  );
};

export const useUpdateFundCost = (fundId: string) => {
  return useApiMutation<
    FundCostCreateResponse,
    APIError,
    { cost: FundCost; data: FundCostUpdate }
  >(({ cost, data }) => fundCostService.update(fundId, cost.id, data), {
    invalidateKeys: (_response, { cost }) => [
      queryKeys.fundCosts.all,
      queryKeys.fundTransactions.lists(fundId),
      queryKeys.fundTransactions.detail(fundId, cost.fund_transaction_id),
      queryKeys.fundTransactions.reconciliation(
        fundId,
        cost.fund_transaction_id
      ),
    ],
    successMessage: 'Cost updated successfully',
  });
};

export const useDeleteFundCost = (fundId: string) => {
  return useApiMutation<void, APIError, string>(
    (costId) => fundCostService.delete(fundId, costId),
    {
      invalidateKeys: [
        queryKeys.fundCosts.all,
        queryKeys.fundTransactions.lists(fundId),
        bankIncomingQueryKeys.all,
      ],
      successMessage: 'Cost deleted successfully',
    }
  );
};

export const useFundCost = (fundId: string, costId: string) =>
  useApiQuery<FundCost>(
    queryKeys.fundCosts.detail(fundId, costId),
    () => fundCostService.get(fundId, costId),
    { enabled: !!fundId && !!costId }
  );
